import { create } from 'zustand';
import { env } from '@/lib/env';
import { queryClient } from '@/lib/query-client';

/**
 * backend-status — the one place that knows whether the API is reachable.
 * Ported from web-v2 `src/stores/backend-status.ts` with its semantics intact.
 *
 * Every request funnels its transport outcome in here (see ./client). The
 * first failure starts a health poll; only a failed probe (or the browser's
 * own offline signal) flips the app to `reconnecting`. The first success flips
 * it back and refetches what went stale while we were dark.
 *
 * THE POLLER IS THE SINGLE PROBER: one timer, one probe in flight, 1s→15s
 * backoff. The client's circuit breaker refuses traffic while this store says
 * the backend is down, so nothing else may test the connection.
 *
 * The probe targets `/health`, which is dependency-free and answers 503 while
 * an instance drains, so "back online" means an instance is really serving.
 */

export type BackendStatus =
  /** Normal. Nothing is shown. */
  | 'online'
  /** A probe confirmed the API is unreachable; we are polling. */
  | 'reconnecting'
  /** The browser itself reports no network. */
  | 'offline'
  /** We were down and just came back — shown briefly, then cleared. */
  | 'recovered';

const HEALTH_PATH = '/health';
const BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 15_000];
const RECOVERED_BANNER_MS = 4_000;
// A sub-5s blip missed nothing worth refetching, and refetch-all never needs
// to run more than once per half minute (web-v2 saw a self-oscillating
// request storm without these floors).
const MIN_OUTAGE_FOR_REFETCH_MS = 5_000;
const REFETCH_ALL_COOLDOWN_MS = 30_000;

interface BackendStatusState {
  status: BackendStatus;
  /** Failed reports since we last saw a healthy response. */
  failures: number;
  lastOkAt: number | null;
  reportReachable: () => void;
  /**
   * `fromProbe` marks a report from the /health probe itself — only
   * probe-confirmed failures flip the visible status, so one slow request
   * can't flash the banner (or arm the breaker) while the API is fine.
   */
  reportUnreachable: (kind: 'offline' | 'unreachable' | 'unavailable', opts?: { fromProbe?: boolean }) => void;
  /** Probe now instead of waiting for the next backoff tick (banner button). */
  checkNow: () => void;
}

let pollTimer: ReturnType<typeof setTimeout> | null = null;
let recoveredTimer: ReturnType<typeof setTimeout> | null = null;
let probeInFlight = false;
let probeConfirmedDown = false;
let darkSince: number | null = null;
let lastRefetchAllAt = 0;

const clearPoll = () => {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
};

export const useBackendStatus = create<BackendStatusState>((set, get) => ({
  status: 'online',
  failures: 0,
  lastOkAt: null,

  reportReachable: () => {
    const { status } = get();
    clearPoll();
    probeConfirmedDown = false;
    if (status === 'online') {
      darkSince = null;
      set({ failures: 0, lastOkAt: Date.now() });
      return;
    }

    const now = Date.now();
    const outageMs = darkSince != null ? now - darkSince : 0;
    darkSince = null;

    if (outageMs < MIN_OUTAGE_FOR_REFETCH_MS) {
      set({ status: 'online', failures: 0, lastOkAt: now });
      return;
    }

    set({ status: 'recovered', failures: 0, lastOkAt: now });
    if (now - lastRefetchAllAt >= REFETCH_ALL_COOLDOWN_MS) {
      lastRefetchAllAt = now;
      void queryClient.refetchQueries({ type: 'active' });
    }

    if (recoveredTimer) clearTimeout(recoveredTimer);
    recoveredTimer = setTimeout(() => {
      if (get().status === 'recovered') set({ status: 'online' });
    }, RECOVERED_BANNER_MS);
  },

  reportUnreachable: (kind, opts) => {
    if (darkSince == null) darkSince = Date.now();
    const failures = get().failures + 1;
    if (opts?.fromProbe) probeConfirmedDown = true;
    if (kind === 'offline' || probeConfirmedDown) {
      set({ status: kind === 'offline' ? 'offline' : 'reconnecting', failures });
    } else {
      set({ failures });
    }
    schedulePoll(failures);
  },

  checkNow: () => {
    clearPoll();
    void probeHealth();
  },
}));

function schedulePoll(failures: number) {
  if (pollTimer) return;
  const delay = BACKOFF_MS[Math.min(failures - 1, BACKOFF_MS.length - 1)] ?? 15_000;
  pollTimer = setTimeout(() => {
    pollTimer = null;
    void probeHealth();
  }, delay);
}

async function probeHealth() {
  if (probeInFlight) return;
  probeInFlight = true;
  const store = useBackendStatus.getState();
  try {
    const response = await fetch(`${env.backendUrl}${HEALTH_PATH}`, {
      method: 'GET',
      cache: 'no-store',
      // The probe must not depend on session state, and a draining instance
      // answers 503 without the CORS credentials handshake.
      credentials: 'omit',
    });
    if (response.ok) {
      store.reportReachable();
      return;
    }
    store.reportUnreachable('unavailable', { fromProbe: true });
  } catch {
    store.reportUnreachable(
      typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'unreachable',
      { fromProbe: true },
    );
  } finally {
    probeInFlight = false;
  }
}

/** Wire the browser's connectivity events. Called once by the reconnect banner. */
export function startBackendStatusWatcher(): () => void {
  const onOffline = () => useBackendStatus.getState().reportUnreachable('offline');
  const onOnline = () => useBackendStatus.getState().checkNow();
  // A tab backgrounded through a deploy verifies on return rather than waiting
  // for a click that then fails.
  const onVisible = () => {
    if (document.visibilityState === 'visible' && useBackendStatus.getState().status !== 'online') {
      useBackendStatus.getState().checkNow();
    }
  };
  window.addEventListener('offline', onOffline);
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisible);
  return () => {
    window.removeEventListener('offline', onOffline);
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisible);
    clearPoll();
  };
}

/** Test seam: reset module state between tests. */
export function __resetBackendStatusForTests() {
  clearPoll();
  if (recoveredTimer) clearTimeout(recoveredTimer);
  probeInFlight = false;
  probeConfirmedDown = false;
  darkSince = null;
  lastRefetchAllAt = 0;
  useBackendStatus.setState({ status: 'online', failures: 0, lastOkAt: null });
}
